import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IBaseSearchRequest, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { CompanyInternalNumberValidator } from 'src/app/shared/validators/company-internal-number.validator';
import { CompanyTenantIdValidator } from 'src/app/shared/validators/company-tenant-id.valiator';
import { ICreateCompanyRequest, ICompany } from '../models/company';
import { CompanyService } from '../services/company.service';
import { ConfirmationDialogService } from '../../shared/services/confirmation-dialog.service';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import * as CompanyActions from "../../stores/global/companies/company-actions";
import { companySelector } from 'src/app/stores/global/companies/company-selectors';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';

@UntilDestroy()
@Component({
    selector: 'app-company-grid',
    templateUrl: './company-grid.component.html',
    styleUrls: ['./company-grid.component.scss'],
    standalone: false
})
export class CompanyGridComponent implements OnInit {

  ////////////////
  // variables //
  //////////////

  @Input() reloadResults : boolean = true;

  public formGroup!: UntypedFormGroup;
  private editedRowIndex!: number;
  public editForm: UntypedFormGroup;

  selectedCompany: ICompany = null;
  maxDateRange = new Date(new Date().setFullYear(new Date().getFullYear() + 3));
  isDialogActive = false;
  vm$: Observable<any>;
  companies$: Observable<ICompany[]>;
  loading: boolean = false;
 
  constructor(
    private route : ActivatedRoute,
    private companyService: CompanyService,
    private appNotificationService: AppNotificationService,
    private confirmationDialogService : ConfirmationDialogService,
    private store: Store<AppStateInterface>
  ) {
    this.companies$ = this.store.pipe(select(companySelector));

    this.vm$ = combineLatest([
      this.gridState$.pipe(),
      this.searchRequest$.pipe(
        switchMap((request) => {
          this.loading = true;
          return this.companyService.listCompanies(request)
        }),
        tap(() => {
          this.loading = false; // Stop loading when data is fetched
        }),
        map(x => {
          var gridData : GridDataResult = {
            data: x.data,
            total: x.totalCount
          }
          return gridData
        }),
        startWith(null)
      ),
      this.searchRequest$.pipe(startWith(null)),
      this.pageChangeSub$.pipe(
        withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
        })),
        tap((x) => {
          const pageNumber = Math.round(
            (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
              x.pageChangeEvent.take
          );
   
          this.gridState$.next({
            skip: x.pageChangeEvent.skip,
            pageSize: x.pageChangeEvent.take,
          });
   
          this.searchRequest$.next({
            ...x.searchRequest,
            pageNumber: pageNumber,
            pageSize: x.pageChangeEvent.take,
          });
        }),
        startWith({ skip: 0, take: 10 })
      )
    ]).pipe(
      map(
        ([
          gridState,
          companies,
          searchRequest,
          pageChangeSub
        ]) => ({
          gridState,
          companies,
          searchRequest,
          pageChangeSub
        })
      )
    );
  }

  ///////////////
  // Getters  //
  /////////////

  get pageSizes() {
    return PageSize;
  }


  ////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBaseSearchRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();

  ngOnInit(): void {
    this.store.dispatch(CompanyActions.getCompanies());
  }

  addHandler({ sender }){
    this.closeEditor(sender);

    this.formGroup = new UntypedFormGroup({
      tenantId: new UntypedFormControl(null, {
        validators: [Validators.required, Validators.pattern('[^äöüÄÖÜ]*')],
        asyncValidators: CompanyTenantIdValidator(this.companyService),
        updateOn: 'blur'}),
      name: new UntypedFormControl(null, {
        validators: Validators.required,
        updateOn: 'blur'}),
      connectionString: new UntypedFormControl(null),
      issuer: new UntypedFormControl(null),
      orderCopyEmail: new UntypedFormControl(null, Validators.email),
      internalNumber: new UntypedFormControl(null, {
        validators: Validators.required,
        asyncValidators: CompanyInternalNumberValidator(this.companyService),
        updateOn: 'blur'}),
    });

    sender.addRow(this.formGroup);
  }

  public saveHandler(event) {
    const request: ICreateCompanyRequest = {
      tenantId: event.dataItem.tenantId.toLowerCase(),
      name: event.formGroup.value.name,
      connectionString: event.dataItem.connectionString ?? null,
      issuer: event.dataItem.issuer ?? null,
      orderCopyEmail: event.dataItem.orderCopyEmail ?? null,
      internalNumber: event.dataItem.internalNumber ?? null,
    }
    
    this.store.dispatch(CompanyActions.createCompany({company: request}));

    event.sender.closeRow(event.rowIndex);
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
  
  public cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  onDelete(company){
    var subscription : Subscription = new Subscription();
    this.confirmationDialogService.open();
    subscription = this.confirmationDialogService.decision$.subscribe(val => {
      if (val) {
        this.store.dispatch(CompanyActions.deleteCompany(company));
        this.confirmationDialogService.updateValue(false);
      } else{
        subscription.unsubscribe();
      }
    });
  }

  onUpgrade(company){
      this.selectedCompany = company;
      this.isDialogActive = true;

      this.editForm = new UntypedFormGroup({
        companyId: new UntypedFormControl(company.id, Validators.required),
        extendedExpiryDate: new UntypedFormControl(new Date(company.validUpto), Validators.required),
      });
  }

  closeDialog(){
    this.selectedCompany = null;
    this.isDialogActive = false;
  }

  onCancel(e: PointerEvent){
    e.preventDefault();
    this.closeDialog();
  }

  public onSave(e: PointerEvent): void {
    e.preventDefault();
    this.store.dispatch(CompanyActions.updateCompany(this.editForm.value));
    this.isDialogActive = false;
  }
}
